import React, { useState } from 'react';
import copy from 'copy-to-clipboard';
import _get from 'lodash/get';
import { client } from 'cccisd-apollo';
import IconBack from 'cccisd-icons/arrow-left8';
import IconCopy from 'cccisd-icons/copy3';
import IconMenu from 'cccisd-icons/menu2';
import IconReport from 'cccisd-icons/file-text';
import Loader from 'cccisd-loader';
import Modal from 'cccisd-modal';
import notification from 'cccisd-notification';
import CccisdTable from 'cccisd-table';
import Tabs from 'cccisd-tabs';
import Tooltip from 'cccisd-tooltip';
import deploymentQuery from './deployments.graphql';
import assessmentQuery from './assessments.graphql';
import style from './style.css';
import reportTemplates from 'js/vendor/reports/reportTemplates/index.js';

const Boilerplate = window.cccisd.boilerplate;
const reportList = _get(window, 'cccisd.appDefs.reports.list', []);

function copyOnClick(url) {
    copy(url);

    notification({
        message: 'Copied to clipboard!',
        type: 'success',
        duration: 4000,
    });
}

const Assessments = ({ allFamilyMembers, family, familyName, getMemberName }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [deploymentData, setDeploymentData] = useState(null);
    const [assessmentData, setAssessmentData] = useState(null);
    const [currReportView, setCurrReportView] = useState(null);

    const familyPawnIds = allFamilyMembers
        .map(member => member.pawn.pawnId)
        .concat([_get(family, 'descendantRoles.metricspawn.pawn.pawnId', -1)]);

    const familyMemberPawnToLabelMap = {};
    allFamilyMembers.forEach(member => {
        familyMemberPawnToLabelMap[member.pawn.pawnId] = getMemberName(member);
    });

    async function getData() {
        setIsLoading(true);
        const deploymentResponse = await client.query({
            query: deploymentQuery,
            variables: {},
            fetchPolicy: 'network-only',
        });

        const parentDeployments = deploymentResponse.data.deployments.deploymentList;
        const parentDeploymentLabelMapper = {};
        const parentDeploymentHandleMapper = {};
        parentDeployments.forEach(parentDeployment => {
            parentDeploymentLabelMapper[parentDeployment.deploymentId] = parentDeployment.label;
            parentDeploymentHandleMapper[parentDeployment.deploymentId] = parentDeployment.deploymentHandle;
        });
        setDeploymentData(parentDeployments);

        const progressResponse = await client.query({
            query: assessmentQuery,
            variables: {
                familyPawnIds,
                parentDeploymentIds: parentDeployments.map(dep => dep.deploymentId),
            },
            fetchPolicy: 'network-only',
        });

        const allAssessmentResults = [];
        const deployments = _get(progressResponse, 'data.deployments.deploymentList', []) || [];
        deployments.forEach(deployment => {
            const apList = _get(deployment, 'assignmentProgressList', []) || [];
            apList.forEach(ap => {
                ap.deploymentLabel = deployment.label;
                if (deployment.deploymentId in parentDeploymentLabelMapper) {
                    ap.deploymentLabel = parentDeploymentLabelMapper[deployment.deploymentId];
                } else if (deployment.parentDeploymentId in parentDeploymentLabelMapper) {
                    ap.deploymentLabel = parentDeploymentLabelMapper[deployment.parentDeploymentId];
                }

                if (deployment.deploymentId in parentDeploymentHandleMapper) {
                    ap.deploymentHandle = parentDeploymentHandleMapper[deployment.deploymentId];
                } else if (deployment.parentDeploymentId in parentDeploymentHandleMapper) {
                    ap.deploymentHandle = parentDeploymentHandleMapper[deployment.parentDeploymentId];
                } else {
                    // don't add if there's no handle
                    return;
                }

                allAssessmentResults.push(ap);
            });
        });

        setAssessmentData(allAssessmentResults);
        setIsLoading(false);
    }

    function renderBody({ closeModal }) {
        if (isLoading || !assessmentData) {
            return <Loader loading />;
        }

        if (!deploymentData || !Array.isArray(deploymentData) || deploymentData.length === 0) {
            return (
                <div className="alert alert-info">
                    <p className="mb-0">No assessments available at this time.</p>
                </div>
            );
        }

        if (currReportView) {
            let report = reportList.find(item => item.id === currReportView.label);
            let reportTemplate = report && reportTemplates.find(item => item.handle === report.reportTemplateHandle);

            return (
                <div>
                    <button
                        className="btn btn-lg btn-link ps-0 mb-4"
                        type="button"
                        onClick={() => setCurrReportView(null)}
                    >
                        <IconBack spaceRight />
                        Back to {familyName}
                    </button>
                    {reportTemplate ? (
                        <reportTemplate.player
                            settings={report.reportTemplateSettings}
                            widgetProps={{
                                pawnId: currReportView.respondentPawnId,
                                pawnHash: currReportView.respondentPawnHash,
                                deploymentId: currReportView.actualDeploymentId,
                            }}
                            variables={{}}
                        />
                    ) : (
                        <div className="alert alert-warning">
                            <p className="mb-0">
                                Report not created for assessment <b>{currReportView.label}</b>.
                            </p>
                        </div>
                    )}
                </div>
            );
        }

        const tabList = [
            {
                name: 'assign',
                title: 'Assign',
                content: (
                    <>
                        <h5>Child Assessments:</h5>
                        <ul className="mb-5">
                            {deploymentData
                                .filter(ap => ap.deploymentHandle.includes('_child_'))
                                .map(deployment => {
                                    const url = Boilerplate.url(
                                        `/assessment/${deployment.deploymentId}/${deployment.hash}`
                                    );

                                    return (
                                        <li key={deployment.deploymentId}>
                                            {deployment.label}&nbsp;
                                            <Tooltip title={'Copy link to ' + deployment.label}>
                                                <span
                                                    role="button"
                                                    onClick={() => copyOnClick(url)}
                                                    className={style.copyBtn}
                                                    tabIndex={0}
                                                >
                                                    <IconCopy spaceLeft />
                                                    <span>Copy Link</span>
                                                </span>
                                            </Tooltip>
                                        </li>
                                    );
                                })}
                        </ul>
                        <h5>Parent Assessments:</h5>
                        <ul className="mb-5">
                            {deploymentData
                                .filter(ap => ap.deploymentHandle.includes('_parent_'))
                                .map(deployment => {
                                    const url = Boilerplate.url(
                                        `/assessment/${deployment.deploymentId}/${deployment.hash}`
                                    );

                                    return (
                                        <li key={deployment.deploymentId}>
                                            {deployment.label}&nbsp;
                                            <Tooltip title={'Copy link to ' + deployment.label}>
                                                <span
                                                    role="button"
                                                    onClick={() => copyOnClick(url)}
                                                    className={style.copyBtn}
                                                    tabIndex={0}
                                                >
                                                    <IconCopy spaceLeft />
                                                    <span>Copy Link</span>
                                                </span>
                                            </Tooltip>
                                        </li>
                                    );
                                })}
                        </ul>
                    </>
                ),
            },
            {
                name: 'results',
                title: 'Results',
                content: (
                    <div className="mb-5">
                        <p className="fst-italic">Click a row in the table to see a report.</p>
                        <CccisdTable
                            name={'assessments_' + familyName}
                            rowKey="assignmentProgressId"
                            showRowActions={false}
                            isAscentOrder={false}
                            perPage={10}
                            showPerPageOptions={false}
                            saveState={false}
                            noRecordsMessage="No assessment results at this time."
                            columns={[
                                {
                                    name: 'dateTime',
                                    label: 'Date',
                                    sort: true,
                                    filter: true,
                                    render: (v, row) => row.date,
                                },
                                { name: 'label', label: 'Assessment', sort: true, filter: true },
                                { name: 'behalf', label: 'Family Member', sort: true, filter: true },
                                { name: 'completedBy', label: 'Completed By', sort: true, filter: true },
                                { name: 'assessmentDisplayScore', label: 'Score' },
                                {
                                    name: 'report',
                                    label: 'Report',
                                    render: (v, row) => {
                                        if (!row.date) {
                                            return <span />;
                                        }

                                        return (
                                            <button
                                                type="button"
                                                className="btn btn-secondary"
                                                onClick={() => setCurrReportView(row)}
                                            >
                                                <IconReport />
                                            </button>
                                        );
                                    },
                                },
                            ]}
                            data={assessmentData.map(ap => {
                                let completedBy = ap?.devTags?.assessmentCompletedByInput;
                                if (!completedBy) {
                                    completedBy = ap?.devTags?.completedByUsername;
                                    if (completedBy) {
                                        const famMember = allFamilyMembers.find(
                                            mem => mem.user.username === completedBy
                                        );
                                        if (famMember) {
                                            completedBy = getMemberName(famMember);
                                        }
                                    }
                                }

                                return {
                                    assignmentProgressId: ap.assignmentProgressId,
                                    dateTime: ap.completedAt || ap.startedAt,
                                    date: ap.completedDate || ap.startedDate,
                                    label: ap.deploymentLabel,
                                    actualDeploymentId: ap.deploymentId,
                                    behalf: ap.devTags.assessmentOnBehalfOf,
                                    completedBy: ap.devTags.assessmentCompletedByInput,
                                    assessmentDisplayScore: ap.devTags.assessmentDisplayScore,
                                    respondentPawnId: ap.pawn.pawn.pawnId,
                                    respondentPawnHash: ap.pawn.pawn.pawnHash,
                                };
                            })}
                        />
                    </div>
                ),
            },
        ];

        return (
            <div>
                <Tabs tabList={tabList} saveInHash />
                <button className="btn btn-secondary" type="button" onClick={() => closeModal()}>
                    Close
                </button>
            </div>
        );
    }

    const trigger = (
        <button type="button" className="btn btn-sm btn-secondary">
            <IconMenu spaceRight />
            Assessments
        </button>
    );

    const title = currReportView ? (
        <span>
            {currReportView.label} for <b>{currReportView.behalf}</b>
        </span>
    ) : (
        <span>
            Assessments for <b>{familyName}</b>
        </span>
    );

    return (
        <Modal
            trigger={trigger}
            beforeShow={getData}
            afterClose={() => setCurrReportView(null)}
            render={renderBody}
            title={title}
            size="large"
        />
    );
};

export default Assessments;
